// QuickStudyConfigView — admin define limites do "tira-dúvida rápido":
// quantidade máxima de imagens, tamanho de cada imagem, e master switch.
// [[project-quickstudy]]

import { useEffect, useState } from "preact/hooks";
import {
  CheckCircle2,
  Coins,
  ImagePlus,
  Loader2,
  Power,
  Save,
  Type
} from "lucide-react";
import { Switch } from "@app/ui/components/switch";
import { api } from "./api";
import { friendlyError } from "@app/ui/lib/friendly-error";
import { openAlert } from "./ConfirmPromptModal";

interface Settings {
  enabled: boolean;
  maxImagesPerMessage: number;
  maxImageSizeMb: number;
  maxTextChars: number;
}

const DEFAULTS: Settings = {
  enabled: true,
  maxImagesPerMessage: 10,
  maxImageSizeMb: 5,
  maxTextChars: 8000
};

export function QuickStudyConfigView() {
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [saved, setSaved] = useState<Settings | null>(null);
  const [draft, setDraft] = useState<Settings>(DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  async function load() {
    setPhase("loading");
    setErrMsg(null);
    try {
      const r = await api.getQuickStudyConfig();
      setSaved(r.settings);
      setDraft(r.settings);
      setPhase("ready");
    } catch (e) {
      setErrMsg(friendlyError(e));
      setPhase("error");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function handleSave() {
    setSaving(true);
    try {
      const r = await api.setQuickStudyConfig(draft);
      setSaved(r.settings);
      setDraft(r.settings);
      setSavedAt(Date.now());
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setSaving(false);
    }
  }

  const isDirty =
    saved !== null &&
    (draft.enabled !== saved.enabled ||
      draft.maxImagesPerMessage !== saved.maxImagesPerMessage ||
      draft.maxImageSizeMb !== saved.maxImageSizeMb ||
      draft.maxTextChars !== saved.maxTextChars);

  if (phase === "loading") {
    return (
      <div class="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando configuração…
      </div>
    );
  }
  if (phase === "error") {
    return (
      <div class="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
        {errMsg ?? "Falha ao carregar."}
      </div>
    );
  }

  return (
    <div class="space-y-6">
      <div class="rounded-md border border-violet-500/30 bg-violet-500/5 p-4">
        <div class="flex items-start gap-3">
          <Coins className="mt-0.5 size-5 shrink-0 text-violet-600" />
          <div class="space-y-1 text-sm">
            <h3 class="font-bold">Limites do Tira-dúvida rápido</h3>
            <p class="text-muted-foreground">
              O Tira-dúvida deixa o aluno fazer perguntas multimodais (texto +
              imagens) com persona fixa por conversa, anti-injection embutido
              e travado em conteúdo acadêmico. Aqui você define quantas
              imagens cada mensagem aceita e qual o tamanho máximo. Mudanças
              levam até 30s pra entrar em vigor (cache do servidor).
            </p>
          </div>
        </div>
      </div>

      <section class="space-y-4 rounded-md border border-border bg-card p-5">
        <header class="flex items-center gap-2">
          <Power className="size-4 text-primary" />
          <h2 class="text-sm font-bold">Master switch</h2>
        </header>
        <label class="flex items-start justify-between gap-3 rounded-md border border-border bg-background/50 px-3 py-2.5">
          <div class="min-w-0 flex-1">
            <div class="text-sm font-semibold">
              {draft.enabled
                ? "Tira-dúvida ATIVO — alunos veem o card, a tab e o dock"
                : "Tira-dúvida DESLIGADO — feature esconde de todos os alunos"}
            </div>
            <div class="mt-0.5 text-[11px] text-muted-foreground">
              Quando desligado, o backend recusa novas mensagens e o frontend
              esconde as superfícies. Conversas existentes ficam no banco.
            </div>
          </div>
          <Switch
            checked={draft.enabled}
            onCheckedChange={(v) => setDraft({ ...draft, enabled: v })}
          />
        </label>
      </section>

      <section class="space-y-4 rounded-md border border-border bg-card p-5">
        <header class="flex items-center gap-2">
          <ImagePlus className="size-4 text-primary" />
          <h2 class="text-sm font-bold">Limites de imagem por mensagem</h2>
        </header>

        <label class="block space-y-1">
          <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Quantidade máxima de imagens
          </span>
          <div class="flex items-center gap-3">
            <input
              type="range"
              min={0}
              max={20}
              step={1}
              value={draft.maxImagesPerMessage}
              onInput={(e) =>
                setDraft({
                  ...draft,
                  maxImagesPerMessage: parseInt((e.target as HTMLInputElement).value, 10)
                })
              }
              class="flex-1"
              aria-label="Quantidade máxima de imagens"
            />
            <span class="w-12 text-right text-sm font-bold">
              {draft.maxImagesPerMessage}
            </span>
          </div>
          <p class="text-[11px] text-muted-foreground">
            Cada imagem cobra ~258 tokens no Gemini, então 10 imagens são
            equivalentes a ~2k tokens só de input por mensagem.
          </p>
        </label>

        <label class="block space-y-1">
          <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Tamanho máximo por imagem (após compressão)
          </span>
          <div class="flex items-center gap-3">
            <input
              type="range"
              min={1}
              max={20}
              step={1}
              value={draft.maxImageSizeMb}
              onInput={(e) =>
                setDraft({
                  ...draft,
                  maxImageSizeMb: parseInt((e.target as HTMLInputElement).value, 10)
                })
              }
              class="flex-1"
              aria-label="Tamanho máximo por imagem"
            />
            <span class="w-12 text-right text-sm font-bold">
              {draft.maxImageSizeMb}MB
            </span>
          </div>
          <p class="text-[11px] text-muted-foreground">
            O frontend comprime cada imagem pra max 1600px no maior lado +
            JPEG q=0.85 antes de enviar — tipicamente fica entre 200KB e 1MB.
            Esse teto cobre casos de tela alta resolução.
          </p>
        </label>
      </section>

      <section class="space-y-4 rounded-md border border-border bg-card p-5">
        <header class="flex items-center gap-2">
          <Type className="size-4 text-primary" />
          <h2 class="text-sm font-bold">Limite de texto</h2>
        </header>

        <label class="block space-y-1">
          <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Caracteres por mensagem
          </span>
          <div class="flex items-center gap-3">
            <input
              type="range"
              min={500}
              max={20000}
              step={500}
              value={draft.maxTextChars}
              onInput={(e) =>
                setDraft({
                  ...draft,
                  maxTextChars: parseInt((e.target as HTMLInputElement).value, 10)
                })
              }
              class="flex-1"
              aria-label="Caracteres por mensagem"
            />
            <span class="w-16 text-right text-sm font-bold">
              {draft.maxTextChars.toLocaleString("pt-BR")}
            </span>
          </div>
          <p class="text-[11px] text-muted-foreground">
            Caracteres = chars literais do textarea. 8.000 já cobre o caso
            "colei um enunciado grande".
          </p>
        </label>
      </section>

      <div class="sticky bottom-4 z-10 flex items-center gap-3 rounded-md border border-border bg-card/95 px-4 py-2 backdrop-blur">
        <button
          type="button"
          onClick={handleSave}
          disabled={!isDirty || saving}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Save className="size-3.5" />
          )}
          {saving ? "Salvando…" : "Salvar alterações"}
        </button>
        {isDirty && (
          <button
            type="button"
            onClick={() => saved && setDraft(saved)}
            disabled={saving}
            class="text-xs font-medium text-muted-foreground hover:text-foreground hover:underline disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancelar
          </button>
        )}
        {savedAt && !isDirty && (
          <span class="inline-flex items-center gap-1 text-[11px] text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="size-3.5" /> salvo
          </span>
        )}
      </div>
    </div>
  );
}
